// Auth helpers — replaces src/lib/auth.js (Firebase version).
//
// No ensureProfile() self-heal complexity needed — the database trigger
// (handle_new_user in 001_schema.sql) creates the profile row automatically
// whenever a user signs up via Supabase Auth.

import { supabase } from './supabase';

// ---------------------------------------------------------------------------
// Sign up — creates an auth user + triggers auto-profile creation in DB.
// ---------------------------------------------------------------------------
export async function signUp({ email, password, displayName, phone }) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { display_name: displayName || email },
      emailRedirectTo: `${window.location.origin}/`
    }
  });
  if (error) throw error;

  // Update phone separately (trigger doesn't have it from metadata).
  if (data.user && (phone || displayName)) {
    await supabase
      .from('users')
      .update({
        phone:        phone        || '',
        display_name: displayName  || email
      })
      .eq('id', data.user.id);
  }

  // session is null when "Confirm email" is on — the user must click the email link first.
  return { user: data.user, session: data.session };
}

// ---------------------------------------------------------------------------
// Sign in
// ---------------------------------------------------------------------------
export async function signIn({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Sign out
// ---------------------------------------------------------------------------
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Password reset email
// ---------------------------------------------------------------------------
export async function resetPassword(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`
  });
  if (error) throw error;
}

// Set a new password for the signed-in (or recovery-link) user.
export async function updatePassword(password) {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Watch auth state — same contract as Firebase onAuthStateChanged.
// Returns an unsubscribe function.
// ---------------------------------------------------------------------------
export function watchAuth(cb) {
  // Fire immediately with the current session.
  supabase.auth.getSession().then(({ data }) => {
    cb(data.session?.user ?? null);
  });

  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    cb(session?.user ?? null);
  });

  return () => subscription.unsubscribe();
}

// ---------------------------------------------------------------------------
// Fetch a user profile from public.users.
// ---------------------------------------------------------------------------
export async function getProfile(uid) {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', uid)
    .single();

  if (error) {
    console.warn('[getProfile] error:', error.message);
    return null;
  }

  // Normalise column names to camelCase so the rest of the app is unchanged.
  return normaliseProfile(data);
}

// ---------------------------------------------------------------------------
// ensureProfile — thin safety net in case the DB trigger didn't fire.
// In practice this is almost never needed.
// ---------------------------------------------------------------------------
export async function ensureProfile(authUser) {
  if (!authUser) return null;

  const existing = await getProfile(authUser.id);
  if (existing) return existing;

  console.warn('[ensureProfile] profile missing — creating fallback for', authUser.email);

  const { count } = await supabase
    .from('users')
    .select('id', { count: 'exact', head: true });

  const role = count === 0 ? 'admin' : 'sales';

  const { data, error } = await supabase
    .from('users')
    .upsert({
      id:           authUser.id,
      email:        authUser.email,
      display_name: authUser.user_metadata?.display_name || authUser.email,
      role,
      disabled:     false
    })
    .select()
    .single();

  if (error) {
    console.warn('[ensureProfile] upsert failed:', error.message);
    return null;
  }

  return normaliseProfile(data);
}

// ---------------------------------------------------------------------------
// Internal — snake_case DB row → camelCase profile object.
// Keeps all existing components (TopBar, ProtectedRoute, LeadDrawer…) unchanged.
// ---------------------------------------------------------------------------
function normaliseProfile(row) {
  if (!row) return null;
  return {
    uid:         row.id,           // alias so existing code using profile.uid still works
    id:          row.id,
    email:       row.email,
    displayName: row.display_name,
    phone:       row.phone,
    role:        row.role,
    disabled:    row.disabled,
    createdAt:   row.created_at,
    updatedAt:   row.updated_at
  };
}
